import { isSameIn, startOf, type Granularity } from '@myre/common-dates';

import {
  type AbstractControl,
  type ValidationErrors,
  type ValidatorFn,
} from '@angular/forms';
import { formatISO, isValid } from 'date-fns';

import { dateFilterValidator } from '../validators/date-filter.validator';
import { dateMaxValidator } from '../validators/date-max.validator';
import { dateMinValidator } from '../validators/date-min.validator';

import { QuestionBase, type QuestionBaseWithoutType } from './question-base';

export type DateErrorType = '>' | '<' | 'filter';
export type DateErrorFormatter = (date: Date, type: DateErrorType) => string;
export type DateFilter = (date: Date) => boolean;
export type DateTriggerIcon = 'myre-icon-calendar' | 'myre-icon-arrow_dropdown';

export function dateLimitValidators(
  questionDate: QuestionDate,
  errorFormatter: DateErrorFormatter = d => formatISO(d),
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    let error = {};

    let granularity: Granularity;

    switch (questionDate.options.type) {
      default:
      case 'date': {
        granularity = 'd';

        break;
      }
      case 'month': {
        granularity = 'M';

        break;
      }
      case 'year': {
        granularity = 'y';

        break;
      }
    }

    const minError = dateMinValidator(
      questionDate.options.min,
      granularity,
      date => errorFormatter(date, '<'),
    )(control);
    const maxError = dateMaxValidator(
      questionDate.options.max,
      granularity,
      date => errorFormatter(date, '>'),
    )(control);

    if (minError) {
      error = minError;
    }

    if (maxError) {
      error = {
        ...error,
        ...maxError,
      };
    }

    if (questionDate.options.dateFilter && isValid(control.value)) {
      const dateFilterError = dateFilterValidator(
        questionDate.options.dateFilter,
        date => errorFormatter(date, 'filter'),
      )(control);

      if (dateFilterError) {
        error = {
          ...error,
          ...dateFilterError,
        };
      }
    }

    return Object.keys(error).length ? error : null;
  };
}

interface IQuestionDateOptions {
  min?: Date;
  max?: Date;
  dateFilter?: DateFilter;
  startAt?: Date;
  type?: 'year' | 'month' | 'date';
  triggerIcon?: DateTriggerIcon;
  errorFormatter?: DateErrorFormatter;
}

export interface IQuestionDate extends QuestionBaseWithoutType<Date> {
  options?: IQuestionDateOptions;
}

export class QuestionDate extends QuestionBase<Date> implements IQuestionDate {
  public get options(): IQuestionDateOptions {
    return this._options;
  }

  public errorFormatter?: DateErrorFormatter;

  private _options: IQuestionDateOptions = {};

  constructor(options: IQuestionDate = {}) {
    super(options);

    let min: Date | undefined;
    let max: Date | undefined;
    let startAt: Date | undefined;
    let type: 'year' | 'month' | 'date';
    let errorFormatter: DateErrorFormatter;
    let dateFilter: DateFilter;

    if (options.options) {
      min = options.options.min ?? undefined;
      max = options.options.max ?? undefined;
      dateFilter = options.options.dateFilter;
      startAt = options.options.startAt || undefined;
      type = options.options.type;
      errorFormatter = options.options.errorFormatter;
    }

    this.elementType = 'date';

    this._options.max = max;
    this._options.min = min;
    this._options.dateFilter = dateFilter;
    this._options.startAt = startAt;
    this._options.type = type;
    this._options.triggerIcon =
      options.options?.triggerIcon ?? 'myre-icon-calendar';
    this.errorFormatter = errorFormatter;

    this._formatValue ??= value => startOf(value, this._getGranularity());
  }

  public override initStart(): void {
    this.validators.push(
      dateLimitValidators(
        this,
        this.errorFormatter ||
          this.questionControlService.dateErrorFormatter.bind(
            this.questionControlService,
          ),
      ),
    );
  }

  public override getValue(): Date {
    const value = super.getValue();

    return value ? this._formatValue(value) : value;
  }

  public override setValue(value: Date): void {
    super.setValue(value ? this._formatValue(value) : value);
  }

  public override equals(value: any): boolean {
    const formValue = this.getValue();

    if (formValue && value) {
      return isSameIn(formValue, value, this._getGranularity());
    }

    return formValue === value;
  }

  public setOptions(
    options: IQuestionDateOptions,
    markAsTouched: boolean = true,
  ): void {
    this._options = options;
    this.validate(markAsTouched);
  }

  private _getGranularity(): Granularity {
    switch (this.options.type) {
      case 'year': {
        return 'y';
      }
      case 'month': {
        return 'M';
      }
      case 'date':
      default: {
        return 'd';
      }
    }
  }
}
